/** Grados de inclinación → roofing pitch (rise/12), como lo usan los roofers. */
export function roofingPitch(deg: number | null | undefined): string | null {
  if (deg == null) return null;
  const rise = Math.round(Math.tan((Number(deg) * Math.PI) / 180) * 12);
  return `${rise}/12`;
}

/**
 * Formatea una fecha "date-only" (columnas `date` de Postgres, p.ej. date_of_loss).
 * pg las serializa como ISO completo ("2024-05-01T00:00:00.000Z"); concatenarle
 * "T12:00:00" daba "Invalid Date". Toma solo YYYY-MM-DD y fija mediodía para
 * evitar el corrimiento de día por timezone.
 */
export function fmtDateOnly(d: string | null | undefined): string | null {
  if (!d) return null;
  const dt = new Date(`${String(d).slice(0, 10)}T12:00:00`);
  return isNaN(dt.getTime()) ? null : dt.toLocaleDateString('en-US');
}

/**
 * HOY, EN LA ZONA DE QUIEN MIRA LA PANTALLA — no en UTC.
 *
 * `new Date().toISOString().slice(0, 10)` devuelve la fecha **UTC**, y eso NO es "hoy" para nadie
 * al oeste de Greenwich a partir de cierta hora. En EDT (UTC−4), de 20:00 a medianoche devuelve
 * MAÑANA: el dashboard de Supplements abría en un día que todavía no existe y mostraba la tarjeta
 * del día vacía, el ranking sin nadie y el botón "volver a hoy" saltando también a mañana. O sea
 * que el que trabaja de noche veía desaparecer su propio trabajo.
 *
 * Se arma a mano en vez de con `toLocaleDateString('en-CA')` para no depender de que el locale
 * devuelva justo el formato ISO.
 */
export function hoyLocal(d: Date = new Date()): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** Formatea un timestamp ISO a fecha + hora locales cortas (ej. "May 1, 3:20 PM"). */
export function fmtDateTime(d: string | null | undefined): string | null {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return null;
  return dt.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** Igual que fmtDateTime pero SIEMPRE en hora del Este (America/New_York) con etiqueta EST/EDT.
 *  La oficina opera en Eastern; los timestamps se guardan en UTC. `locale` para el mes bilingüe. */
export function fmtDateTimeEastern(d: string | null | undefined, locale?: string): string | null {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return null;
  return dt.toLocaleString(locale, {
    timeZone: 'America/New_York',
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  });
}

/** Formatea centavos a USD: 121606 -> "$1,216.06". */
export function formatUsd(cents: number | string | null | undefined): string {
  const n = Number(cents ?? 0);
  const neg = n < 0;
  const abs = Math.abs(n);
  const dollars = Math.floor(abs / 100).toLocaleString('en-US');
  const rem = String(abs % 100).padStart(2, '0');
  return `${neg ? '-' : ''}$${dollars}.${rem}`;
}

/**
 * LOCALE DE LA INTERFAZ para fechas y horas (auditoría 2026-09-15): había 327 `'en-US'` fijos en
 * 154 archivos y un usuario en español veía "Sep 15, 3:20 PM". Lo fija el I18nProvider al cambiar
 * de idioma; las fechas se formatean con `toLocaleDateString(localeUI(), …)`. Los MONTOS siguen
 * en en-US a propósito: es una empresa de EE. UU. y el separador de miles no cambia por idioma.
 */
let localeActual = 'en-US';
export function localeUI(): string { return localeActual; }
export function setLocaleUI(lang: 'en' | 'es'): void { localeActual = lang === 'es' ? 'es-419' : 'en-US'; }
